import { RefObject, useEffect, useRef, useState } from 'react';

// True when the user prefers reduced motion, so animations/smooth scrolling can
// be suppressed. Guarded for non-browser (test) environments.
export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Scroll a scroll container to its own bottom, honouring reduced-motion. Unlike
// scrollIntoView on an end-marker, this only moves the container itself and
// never bubbles up to scroll ancestors (which would drag the whole live page
// down while the presenter is watching the slides).
export const scrollContainerToBottom = (el: HTMLElement | null): void => {
  if (!el) return;
  el.scrollTo({ top: el.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
};

const NEAR_BOTTOM_PX = 80;

// Tracks whether a scrollable element is currently near its bottom, so callers
// can auto-follow new content only when the reader hasn't scrolled up to reread.
// Defaults to true so the first render follows to the latest content.
export const useNearBottom = (ref: RefObject<HTMLElement>): boolean => {
  const [nearBottom, setNearBottom] = useState(true);
  const nearRef = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const next = distance <= NEAR_BOTTOM_PX;
      if (next !== nearRef.current) {
        nearRef.current = next;
        setNearBottom(next);
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [ref]);

  return nearBottom;
};
